// Глобальные axios-перехватчики для админпанели.
//
// Все Service-файлы (UserService, AdminService, CommentsService) используют
// дефолтный экспорт axios — поэтому interceptor на самом модуле сработает
// для каждого их запроса автоматически, без правки 400+ мест.
//
// Что делаем:
//  1) Перехват 401 (истёкший/невалидный токен) и 403 (нет прав):
//     - чистим localStorage,
//     - редиректим на /login,
//     - НЕ показываем технический axios-error, а отдаём чистую ошибку наверх.
//  2) Перехват сетевых ошибок (нет интернета, бэк недоступен):
//     - logger.error для диагностики,
//     - дальше обычная Promise.reject — компонент сам покажет toast.
//
// Подключается ОДИН раз в src/index.js.

import axios from 'axios';
import logger from '../utils/logger';
// Чтобы не редиректить бесконечно: если упало в момент, когда мы УЖЕ на /login —
// просто пробрасываем ошибку, без второго перехода.
function isOnLoginPage() {
    return window.location.pathname === '/login';
}

// Защита от шторма: если бэк прислал 401 несколько раз подряд (например,
// 5 параллельных запросов на главной), не хотим логировать пятикратно.
let loggedOutAlready = false;
/* Жив ли ещё наш токен по собственному сроку (exp внутри JWT).
 *
 * 🔴 21.09.2026. Админка выкидывала на /login «каждые 5 минут», хотя токен
 * user-сервиса живёт 24 ЧАСА. Причина: разлогинивал ЛЮБОЙ одиночный 401 —
 * а он прилетает не только от протухшего токена: сбой прокси, перезапуск
 * сервиса, случайная 401 от второстепенной ручки. Токен при этом живой.
 *
 * Теперь смотрим на сам токен: если по exp он ещё действителен — это не
 * наша сессия умерла, а конкретный запрос не удался. Чистим только когда
 * токен реально истёк (или нечитаем). */
function tokenLooksAlive() {
    const t = localStorage.getItem('Authorization');
    if (!t) return false;
    try {
        const body = t.split('.')[1];
        if (!body) return false;
        const json = JSON.parse(
            decodeURIComponent(escape(atob(body.replace(/-/g, '+').replace(/_/g, '/'))))
        );
        if (typeof json.exp !== 'number') return false;   // нет exp — судить не можем
        // небольшой запас: токен на грани считаем мёртвым
        return json.exp * 1000 - Date.now() > 10_000;
    } catch (_) {
        return false;   // не JWT или битый — доверяем 401
    }
}

// Grace-период после загрузки страницы (3с): не разлогиниваем по 401, даже
// если что-то ответило ошибкой. Это спасает от гонки между AuthProvider.init,
// Login.jsx, parallel-запросами /profile/role/etc — пока всё инициализируется,
// один 401 в стае не должен ронять валидную сессию.
// 2026-05-29: ввёл после инцидента когда после успешного входа сразу выкидывало на /login.
const PAGE_LOAD_AT = Date.now();
function inGracePeriod() {
    return (Date.now() - PAGE_LOAD_AT) < 3000;
}

// 401 на login-эндпоинте = неправильные данные, НЕ разлогин.
// Эти URL не должны триггерить очистку токена.
function isLoginAttempt(url) {
    if (!url) return false;
    return url.includes('/profile/login') ||
           url.includes('/auth/yandex/exchange') ||
           url.includes('/auth/google/exchange');
}

// 2026-05-31: эти эндпоинты могут отдавать 401 в норме —
// fallback в AuthContext.refreshAccess использует /users/profile
// для определения прав. Если admin-сервис лежит или JWT-секреты
// разъехались — ловим 401 и спокойно продолжаем по fallback.
// БЕЗ этого интерсептор чистил токен и кидал на /login прямо
// после успешного login → юзер не мог зайти.
function isSoftAuthCheck(url) {
    if (!url) return false;
    return url.includes('/admin/access-check');
}

export function setupAxios() {
    axios.interceptors.response.use(
        // Успешный ответ — пропускаем как есть.
        (response) => response,

        // Ошибочный ответ.
        (error) => {
            // Сетевая ошибка (бэк недоступен, нет интернета)
            if (!error.response) {
                logger.error('[axios] network error:', error?.message || error);
                return Promise.reject(error);
            }

            const status = error.response.status;
            const url = error.config?.url || '';

            if (status === 401) {
                // 1) Не разлогиниваем если запрос — попытка логина (это валидная ошибка login)
                if (isLoginAttempt(url)) {
                    return Promise.reject(error);
                }
                // 2) Не разлогиниваем по «мягким» проверкам прав (access-check).
                //    Они допускают 401 в норме — caller сделает fallback на профиль.
                if (isSoftAuthCheck(url)) {
                    logger.warn(`[axios] 401 на soft-auth ${url} — токен НЕ чищу, fallback в caller`);
                    return Promise.reject(error);
                }
                // 3) Не разлогиниваем в первые 3 секунды после загрузки страницы
                if (inGracePeriod()) {
                    logger.warn(`[axios] 401 на ${url} в grace-period — токен НЕ чищу`);
                    return Promise.reject(error);
                }
                // 4) Если в localStorage нет токена — выкидывать некуда, уже разлогинены
                if (!localStorage.getItem('Authorization')) {
                    return Promise.reject(error);
                }
                // 5) Сессию отозвали на сервере (выход на другом устройстве,
                //    «выйти везде», бан) — user-сервис явно говорит об этом
                //    кодом TOKEN_REVOKED. Это настоящий разлогин: чистим
                //    токен, даже если по exp он формально ещё живой.
                const code = error.response?.data?.code;
                if (code === 'TOKEN_REVOKED') {
                    logger.warn('[axios] сессия отозвана на сервере — разлогиниваю');
                } else if (tokenLooksAlive()) {
                    // Токен ещё НЕ истёк по своему exp → это не смерть сессии,
                    // а неудача конкретного запроса (сбой прокси, перезапуск
                    // сервиса, 401 от второстепенной ручки). Раньше такой
                    // одиночный 401 выкидывал на /login посреди работы.
                    logger.warn(`[axios] 401 на ${url}, но токен ещё жив по exp — НЕ разлогиниваю`);
                    return Promise.reject(error);
                }
                if (!loggedOutAlready && !isOnLoginPage()) {
                    loggedOutAlready = true;
                    logger.warn(`[axios] 401 на ${url}: чищу токен и редиректю на /login`);
                    localStorage.removeItem('Authorization');
                    window.location.replace('/login');
                }
            }

            // Все остальные ошибки — отдаём наверх компоненту, он сам решит, что делать.
            return Promise.reject(error);
        }
    );

    logger.log('[axios] interceptors set up');
}

// Если приложение перезагрузится после успешного логина, флаг должен сбрасываться.
export function resetLogoutGuard() {
    loggedOutAlready = false;
}
